/**
 * unwrapResponse — Safely extract data from API responses regardless of shape.
 *
 * Backend endpoints are inconsistent: some return `{ data: [...] }` (wrapped),
 * others return raw arrays or objects. This utility normalizes both shapes.
 *
 * Usage:
 *   const items = unwrapResponse<MyType[]>(resp.data);
 *   const item = unwrapResponse<MyType>(resp.data);
 *
 * Extraction priority:
 *   1. resp.data.data (wrapped format)
 *   2. resp.data (raw format — array or object)
 *   3. fallback (default: undefined)
 */

/**
 * Extract the payload from an API response, handling both wrapped and raw formats.
 *
 * @param responseData - The `response.data` from axios (already unwrapped from HTTP envelope)
 * @param fallback - Optional fallback value if extraction fails
 * @returns The extracted data, or fallback
 */
export function unwrapResponse<T>(responseData: unknown, fallback?: T): T {
  if (responseData === null || responseData === undefined) {
    return (fallback ?? responseData) as T;
  }

  // If it's an array, it's already the raw data
  if (Array.isArray(responseData)) {
    return responseData as T;
  }

  // If it's an object with a `data` key, unwrap it
  if (typeof responseData === 'object' && 'data' in (responseData as Record<string, unknown>)) {
    return (responseData as Record<string, unknown>).data as T;
  }

  // Otherwise return as-is (raw object response)
  return responseData as T;
}

/**
 * Extract an array from an API response, guaranteeing an array return type.
 * Falls back to empty array if extraction fails.
 */
export function unwrapArray<T>(responseData: unknown): T[] {
  const result = unwrapResponse<T[]>(responseData, []);
  return Array.isArray(result) ? result : [];
}
